import _ from 'lodash';
import app, { appIds } from '../../../app';
import routerHelper from '../../../helpers/router';
import projectsModel from '../../../models/projects';
import constantsCommon from '../../../helpers/constants';
import multiViewsProjectsModel from '../../../models/multiViewsProjects';
import bindStatusAndProgressHelper from '../../../helpers/bindStatusAndProgress';
import globalStore from '../../../store/main';
import { checkPricingAccess } from '../../../helpers/pricingHelper';
import rights from '../../../components/rights';

import TableColumns from './ListViewColumns';
import TableTasks from './ListViewTasks';

const {
  events: {
    ID_EVENT_COMMENTS_ADD_COMMENT,
    ID_EVENT_COMMENTS_REMOVE_COMMENT,
    ID_EVENT_COMMENTS_MARK_AS_READ,
    ID_EVENT_TASKS_MASSDELETE,
  },
} = appIds;

class ListView {
  get tasks() {
    return this.tableTasks.tasks;
  }

  get showColumns() {
    return this.tableColumns.showColumns;
  }

  get dynamicColumns() {
    return this.tableColumns.dynamicColumns;
  }

  get staticColumns() {
    return this.tableColumns.staticColumns;
  }

  get prependColumns() {
    return this.tableColumns.prependColumns;
  }

  get config() {
    return this.tableColumns.config;
  }

  constructor() {
    this.project_id = null;
    this.multiview_id = null;
    this.ganttIDs = [];

    this.eventsIds = [];
    this.tasksModelEventsIds = [];

    this.projectsWithCostRight = [];

    this.tableTasks = null;
    this.tableColumns = null;

    this.fromListCreateTask = false;
  }

  get pricingAccesToCost() {
    return checkPricingAccess('costs');
  }

  async init() {
    this.tableTasks = new TableTasks();
    this.tableColumns = new TableColumns();
    this._updateGanttIds();

    await this.tableTasks.addTasksByProjectIDs(this.ganttIDs);
    this.tableTasks.updateDataForBreadCrumbs();

    await this.tableColumns.init(this.ganttIDs, !!this.project_id);

    this.addListheners();

    if (routerHelper.isListViewRoute()) {
      projectsModel.forceSetActive = true;
    }
  }

  static _defineGanttIds() {
    let ganttIDs = [];
    let project_id = null;
    let multiview_id = null;
    const currentRoute = routerHelper.getCurrentRoute();

    if (routerHelper.isListViewRoute()) {
      ganttIDs = projectsModel.getAllProjects().map(p => p.gantt_id);
    }

    if (currentRoute.path.includes('projects/')) {
      ganttIDs = multiViewsProjectsModel.getActiveViewData().ganttIDs;
      multiview_id = multiViewsProjectsModel.getActiveGanttId().id;
    }

    if (currentRoute.path.includes('project/')) {
      project_id = +routerHelper.getCurrentRoute().params.projectId;
      ganttIDs = [+project_id];
    }

    if (!ganttIDs.length) return null;

    ganttIDs = ganttIDs.filter(gantt_id => !projectsModel.isArchived(gantt_id));

    return { ganttIDs, project_id, multiview_id };
  }

  static hasUserTask() {
    const projects = ListView._defineGanttIds();

    if (!projects) return false;

    const ganttIds = projects.ganttIDs;

    return ganttIds.some(ganttId => {
      const tasks = TableTasks._getFiltratedTasksByRight(ganttId);

      if (tasks.length) return true;

      return false;
    });
  }

  _updateGanttIds() {
    const projects = ListView._defineGanttIds();

    this.ganttIDs = projects?.ganttIDs || [];
    this.project_id = projects?.project_id || null;
    this.multiview_id = projects?.multiview_id || null;

    this.tableColumns.updateGanttIds(this.ganttIDs);
    this.tableTasks.updateGanttIds(this.ganttIDs);

    this._checkCostRightsOnProjects();
  }

  _checkCostRightsOnProjects() {
    if (!this.pricingAccesToCost) {
      this.projectsWithCostRight.splice(0);
    }

    this.ganttIDs.forEach(gantt_id => {
      const hasRight = rights.project.hasCostRight(gantt_id);
      const index = this.projectsWithCostRight.indexOf(gantt_id);

      if (hasRight && index === -1) {
        this.projectsWithCostRight.push(gantt_id);
      }

      if (!hasRight && index !== -1) {
        this.projectsWithCostRight.splice(index, 1);
      }
    });

    this.projectsWithCostRight.length
      ? this.tableColumns.addCostColumns()
      : this.tableColumns.deleteCostColumns();
  }

  addListheners() {
    addAppEvent('afterGanttWorkerFinish', this.onAfterGanttWorker, this);
    addAppEvent('onAfterCollaboration', this.onAfterCollaboration, this);
    addAppEvent('project:archive', this.onArhiveProject, this);
    addAppEvent('updateListViewTasksState', this.onUpdateTask, this);
    addAppEvent('userCustomValuesModel:change', this.onChangeCustomColumn, this);
    addAppEvent(ID_EVENT_COMMENTS_ADD_COMMENT, this.onUpdateComments, this);
    addAppEvent(ID_EVENT_COMMENTS_REMOVE_COMMENT, this.onUpdateComments, this);
    addAppEvent(ID_EVENT_COMMENTS_MARK_AS_READ, this.onUpdateComments, this);
    addAppEvent('taskSettings:delete', this.onDeleteTaskFromTable, this);
    addAppEvent('taskSettings:delete:commonList', this.deleteTask, this);
    addAppEvent('tasks:model:updateTask', this.onUpdateTask, this);
    addAppEvent('project:component:remove', this.onDeleteProject, this);
    addAppEvent('tasksModel:onStoreUpdate', this.onStoreUpdatedTasks, this);
    addAppEvent('massChange:update:customValues', this.onUpdateCustomValues, this);
    addAppEvent(ID_EVENT_TASKS_MASSDELETE, this.onBulkDeleteTasks, this);

    function addAppEvent(eventName, eventHandler, context) {
      const e_id = app.on(eventName, eventHandler.bind(context));

      context.eventsIds.push(e_id);
    }
  }

  onBulkDeleteTasks(ganttId, tasksId) {
    this._deleteTasksFromTable(tasksId);
  }

  onUpdateCustomValues(params) {
    const { projectId, tasksId } = params;

    if (projectId) {
      this._updateTasksByProjects([projectId]);
    }
  }

  onDeleteProject(gantt_id) {
    this._deleteTasksFromTableByProjectIds([+gantt_id]);
  }

  onAfterGanttWorker(data) {
    data.tasks.forEach(t => this._updateTaskById(+t.id));
    app.trigger('gantt:progress:hide');
  }

  onAfterCollaboration(e) {
    const updByTasks = ['TaskCommentCreated'].includes(e.event); // , 'TaskCommentDeleted'

    if (updByTasks) {
      e.tasks.forEach(task_id => this._updateTaskById(+task_id));
    }

    if (e.event === 'ProjectUpdated') {
      this._hasUpdatesForCurrentProject(e.projects)
      && this.tableTasks.updateDataProjectName();
    }

    if (e.event === 'CustomFieldUpdated') {
      this.tableColumns.updateAllColumns();
      this._updateTasksByProjects(e.projects);
    }

    if (e.event === 'CustomFieldDeleted') {
      e.customFields.forEach(columnId => {
        this.tableColumns.deleteCustomColumnFromProjects(columnId);
      });
    }

    const isCommonListview = !this.project_id && !this.multiview_id;

    if (e.event === 'ProjectCreated' && isCommonListview) {
      this._addTasksToTableByProjects(e.projects);
      app.trigger('refresh:filter');
    }

    if (e.event === 'ProjectDeleted') {
      this._deleteTasksFromTableByProjectIds(e.projects);
    }

    if (e.event === 'TaskDeleted') {
      this._deleteTasksFromTable(e.tasks);
    }

    if (e.event === 'TaskUpdated') {
      if (!this._hasUpdatesForCurrentProject(e.projects)) return;

      this.tableTasks.updateDataForBreadCrumbs();
      this._updateTasksByProjects(e.projects);
    }

    if (e.event === 'TaskAttachementCreated'
      || e.event === 'TaskAttachementDeleted'
    ) {
      e.tasks.forEach(task_id => this._updateTaskById(+task_id));
    }

    if (e.event === 'TaskCreated') {
      e.tasksData.forEach(task => {
        if (this.ganttIDs.includes(+task.projectId)) {
          this.tableTasks.addTaskById(+task.id);
        }
      });
      app.trigger('refresh:filter');
    }

    if (e.event === 'TaskCustomFieldUpdated') {
      this._updateTasksByProjects(e.projects);
    }

    if (e.event === 'CustomFieldToProjectAssigned') {
      let projectIds = [];

      e.assignedCustomFields.forEach(custom => {
        projectIds.push(...custom.projectIds);
      });

      projectIds = [...new Set(projectIds)];
      const curentProject = projectIds.filter(id => this.ganttIDs.includes(id));

      this._updateTasksByProjects(curentProject);

      this.tableColumns.updateAllColumns();
      app.trigger('refresh:filter');
    }

    if (e.event === 'CustomFieldFromProjectUnassigned') {
      this.tableColumns.updateAllColumns();
    }

    if (e.event === 'ResourceOnProjectsUpdated'
      || e.event === 'TaskResourceAssigned'
      || e.event === 'TaskResourceUnassigned'
    ) {
      this._updateTasksByProjects(e.projects);
      app.trigger('refresh:filter');
    }

    if (e.event === 'ResourceFromProjectsUnassigned'
      || e.event === 'ResourceToProjectsAssigned'
      || e.event === 'HistoryRestored'
      || e.event === 'ResourcesOnProjectsConverted'
    ) {
      this.tableColumns.updateAllColumns();
      this._updateTasksByProjects(e.projects);
      app.trigger('refresh:filter');
    }

    if (e.event === 'ResourceRoleUpdated'
      || e.event === 'ResourceDeleted'
    ) {
      this.tableColumns.updateAllColumns();
      this._updateTasksByProjects(this.ganttIDs);
      app.trigger('refresh:filter');
    }
  }

  onArhiveProject(project_id, isArchived) {
    if (this.project_id || this.multiview_id) return;

    if (isArchived) {
      this._deleteTasksFromTableByProjectIds([project_id]);
    } else {
      this._addTasksToTableByProjects([project_id]);
    }
    app.trigger('refresh:filter');
  }

  onDeleteTaskFromTable(ids) {
    this._deleteTasksFromTable(ids);
  }

  onChangeCustomColumn(task) {
    if (!task) return;
    this.tableTasks.updateTaskById(+task.id);
  }

  onUpdateComments(comment, taskId) {
    this._updateTaskById(taskId);
  }

  onUpdateTask(taskId, taskDataOrGanttId) {
    if (!taskId || !taskDataOrGanttId) return;

    const ganttId = typeof taskDataOrGanttId === 'object' ? taskDataOrGanttId.gantt_id : +taskDataOrGanttId;

    if (!this._hasUpdatesForCurrentProject([ganttId])) return;

    this._updateTaskById(taskId);
  }

  onStoreUpdatedTasks(params) {
    const { newTaskId, ganttId } = params;

    if (newTaskId) {
      const task = this.tableTasks.addTaskById(+newTaskId);

      if (task) {
        globalStore.commit('listView/pushToFiltredTasks', task);
      }
      this._updateTaskById(newTaskId);
    }

    if (ganttId) {
      this._updateTasksByProjects([ganttId]);
    }
  }

  async deleteTask(task) {
    this._deleteTasksFromTable([+task.id]);

    if (gantt.isTaskExists(task.id)) {
      gantt.deleteTask(task.id);
    } else {
      await gantt.ganttWorker.calculate([task], 'changeTasks', 'removeTasks');
      globalStore.dispatch('tasksModel/backgroundDeleteTask', task);
    }

    return true;
  }

  _updateTasksByProjects(projectIds) {
    if (!projectIds || !projectIds.length) return;

    this._updateGanttIds();

    projectIds.forEach(ganttId => {
      if (this.ganttIDs.includes(ganttId)) {
        this.tableTasks.updateTasksByProjectId(ganttId);
      } else {
        this._deleteTasksFromTableByProjectIds([ganttId]);
      }
    });
  }

  _hasUpdatesForCurrentProject(gantt_ids) {
    return gantt_ids.find(id => this.ganttIDs.includes(id));
  }

  _addTasksToTableByProjects(projectIds) {
    const noAdded = [];

    projectIds.forEach(id => {
      if (!this.ganttIDs.includes(id)) {
        noAdded.push(id);
      }
    });
    this.tableTasks.addTasksByProjectIDs(noAdded);

    this._updateGanttIds();
  }

  _deleteTasksFromTableByProjectIds(projectIds) {
    if (!projectIds.length) return;

    this._updateGanttIds();

    this.tableTasks.deleteTasksByProjectIDs(projectIds);
  }

  _deleteTasksFromTable(ids) {
    this.tableTasks.deleteTasksById(ids);
  }

  _updateTaskById(taskId) {
    if (!taskId) return;
    this.tableTasks.updateTaskById(+taskId);
  }

  createItem(item) {
    gantt.config.creatingMilestoneFlag = item.type === 'milestone';
    this.fromListCreateTask = true;

    const parent_id = globalStore.getters['tasksModel/getTotalEstimateDataForProject'](this.project_id).id;

    app.trigger('createNewTask', parent_id, item.name);
  }

  async changeTask(edit) {
    let updateByWorker = ['start_date', 'end_date', 'duration', 'progress'].find(i => i === edit.property);
    const originalTaskData = globalStore.getters['tasksModel/getTaskByGanttId'](edit.task.gantt_id, edit.task.id);
    const task = _.cloneDeep(originalTaskData);
    const value = this.validateValue(edit.value, edit.property, originalTaskData);

    task[edit.property] = value;

    if (edit.property === 'status') {
      task.progress = bindStatusAndProgressHelper.calc(value, null).progress;
      if (task.progress !== originalTaskData.progress) updateByWorker = true;
    }

    if (updateByWorker) {
      await gantt.ganttWorker.calculate([task], 'changeTasks', 'updateTasks');
    } else {
      await globalStore.dispatch('tasksModel/backgroundUpdateTask', { taskData: task });
    }

    switch (edit.property) {
    case 'progress':
      task.status = bindStatusAndProgressHelper.calc(null, value * 100).status;
      await globalStore.dispatch('tasksModel/changeStatus', {
        statusValue: task.status,
        taskData: task,
      });
      break;
    case 'status':
      await globalStore.dispatch('tasksModel/changeStatus', {
        statusValue: value,
        taskData: task,
      });
      break;
    case 'priority':
      await globalStore.dispatch('tasksModel/changePriority', {
        priorityValue: value,
        taskData: task,
      });
      break;
    case 'duration':
      app.trigger('changedTaskDuration', task, originalTaskData);
      break;
    case 'estimation':
      app.trigger('changedTaskEstimation', task, true);
      break;
    }

    Object.assign(edit.task, task);
  }

  validateValue(value, field, oldTask) {
    const old_value = oldTask[field];

    if (field === 'progress') return validateProgress(value);
    if (field === 'duration') return validateDuration(value, old_value);
    if (field === 'end_date') return validateEndtDate(value, oldTask);

    return value;

    function validateProgress(value) {
      value = (Math.round(value * 100) || 0) / 100;
      value = value > 1 ? 1 : value;
      value = value < 0 ? 0 : value;

      return value;
    }

    function validateDuration(val, old_val) {
      let value = Math.round(val);
      const limit = constantsCommon.DURATION_LIMITS.minute;

      value = value < 1 ? old_val : value;
      value = value > limit ? limit : value;

      value = +value || old_val;

      return value;
    }

    function validateEndtDate(val, oldTask) {
      const end = new Date(val).getTime();
      const start = new Date(oldTask.start_date).getTime();

      return end < start ? oldTask.end_date : val;
    }
  }

  changeTaskCustom(edit) {
    const originalTaskData = globalStore.getters['tasksModel/getTaskByGanttId'](edit.task.gantt_id, edit.task.id);
    // edit.task[edit.column.property] = edit.value;

    let value = typeof edit.value === 'undefined' || edit.value == null ? '' : edit.value;

    if (edit.column.isOptions) {
      if (Array.isArray(value)) {
        value = value.map(i => i.id).join(',');
      }
      if (_.isObject(value)) {
        value = value.id.toString();
      }
    }

    gantt.callEvent('onChangeUserCustomValue', [originalTaskData, edit.column.id, value]);
  }

  changeColumnToShow(data) {
    this.tableColumns.changeColumnToShow(data);
  }

  destroy() {
    this.eventsIds.forEach(id => app.off(id));
    this.tableTasks = null;
    this.tableColumns = null;
  }
}

export default new ListView();
